import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import * as Sentry from '@sentry/react';

const API_URL = import.meta.env.VITE_API_URL;

const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const isPublicEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  const publicEndpoints = [
    '/api/dashboard/projects/public',
    '/api/projects/structure/get',
    '/api/layers/features/selected',
    '/api/layers/attributes',
    '/api/layers/column/values',
    '/api/plots/config',
    '/api/projects/wypis/',
  ];
  return publicEndpoints.some(endpoint => url.includes(endpoint));
};

export class NetworkError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = 'NetworkError';
    this.code = code;
  }
}

const clearAuth = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.dispatchEvent(new Event('auth:logout'));
};

const attachInterceptors = (client: AxiosInstance) => {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.config.responseType === 'blob') {
        return response;
      }

      if (
        response.data &&
        typeof response.data === 'object' &&
        'data' in response.data &&
        'status' in response.data &&
        !('success' in response.data)
      ) {
        response.data = response.data.data;
      }

      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('Request timeout:', originalRequest?.url);
        Sentry.captureException(new NetworkError('API Timeout', 'TIMEOUT'), {
          tags: { api_url: originalRequest?.url, error_type: 'timeout' },
        });
        return Promise.reject(new NetworkError(
          'Serwer nie odpowiada. Sprawdź połączenie z internetem lub spróbuj ponownie później.',
          'TIMEOUT'
        ));
      }

      if (!error.response && error.message === 'Network Error') {
        console.error('Network error:', originalRequest?.url);
        Sentry.captureException(new NetworkError('API Network Error', 'NETWORK_ERROR'), {
          tags: { api_url: originalRequest?.url, error_type: 'network' },
        });
        return Promise.reject(new NetworkError(
          'Brak połączenia z serwerem. Sprawdź połączenie z internetem.',
          'NETWORK_ERROR'
        ));
      }

      if (error.response) {
        const status = error.response.status;

        if (status >= 500) {
          Sentry.captureException(error, {
            tags: {
              api_url: originalRequest?.url,
              http_status: String(status),
              error_type: 'server_error',
            },
            contexts: {
              response: {
                status,
                data: error.response.data
                  ? (typeof error.response.data === 'string'
                      ? error.response.data.slice(0, 500)
                      : JSON.stringify(error.response.data).slice(0, 500))
                  : 'No response data',
              },
            },
          });
        }

        if (status === 401 && !originalRequest._retry) {
          const refreshToken = localStorage.getItem('refreshToken');
          const accessToken = localStorage.getItem('accessToken');

          if (!refreshToken || originalRequest.url?.includes('/api/auth/')) {
            if (!accessToken && isPublicEndpoint(originalRequest.url)) {
              return Promise.reject(error);
            }
            clearAuth();
            return Promise.reject(error);
          }

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return client(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const response = await axios.post(`${API_URL}/api/auth/token/refresh`, {
              refresh: refreshToken
            });

            const responseData = response.data.data ?? response.data;
            const newAccessToken = responseData.access;
            localStorage.setItem('accessToken', newAccessToken);

            if (responseData.refresh) {
              localStorage.setItem('refreshToken', responseData.refresh);
            }

            processQueue(null, newAccessToken);

            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return client(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            clearAuth();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else if (status === 403) {
          const body = error.response.data as { code?: string } | undefined;
          if (body && body.code === 'PASSWORD_CHANGE_REQUIRED') {
            const currentPath = window.location.pathname;
            if (currentPath !== '/change-password') {
              window.location.href = '/change-password';
            }
          } else {
            console.warn('403 Forbidden - insufficient permissions');
          }
        }

        const req = originalRequest as InternalAxiosRequestConfig & {
          _lockRetried?: boolean;
          _503RetryCount?: number;
        };

        if (status === 423 && !req._lockRetried) {
          req._lockRetried = true;
          await new Promise(r => setTimeout(r, 5000));
          return client(req);
        }

        if (status === 503) {
          const count = req._503RetryCount ?? 0;
          if (count < 3) {
            req._503RetryCount = count + 1;
            const delay = Math.min(1000 * 2 ** count, 4000);
            await new Promise(r => setTimeout(r, delay));
            return client(req);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

attachInterceptors(apiClient);

export default apiClient;

export const apiClientFormData = axios.create({
  baseURL: API_URL,
  timeout: 300000,
});

attachInterceptors(apiClientFormData);
